import {ExtendedFloatPaneOptions as FloatPaneOptions, FloatPaneView} from '../interfaces';

type IPosition = FloatPaneOptions['positionInfo'];

// https://navermaps.github.io/maps.js.ncp/docs/tutorial-6-CustomOverlay.html 참고
// floatPane은 z-index와 상관 없이 모든 오버레이의 최상위에 위치하는 오버레이입니다.
export function FloatPane(this: FloatPaneView, options: FloatPaneOptions) {
  if (options.content) {
    const div = document.createElement('div');
    div.innerHTML = options.content;
    this._element = div;
  }

  this.setPosition(options.positionInfo);
  this.setZIndex(options.zIndex);
  this.setMap(options.map || null);

  this._draggable = options.draggable;
}

FloatPane.prototype = new (window.naver?.maps?.OverlayView ?? Object)() as FloatPaneView;
FloatPane.prototype.constructor = FloatPane;

FloatPane.prototype.setPosition = function (position: IPosition) {
  this._position = position;
  this.draw();
};

FloatPane.prototype.getPosition = function () {
  return this._position;
};

FloatPane.prototype.onAdd = function () {
  // https://navermaps.github.io/maps.js.ncp/docs/naver.maps.Map.html#toc38__anchor
  if (this._element) {
    this.getPanes().floatPane.append(this._element);
    this._element.addEventListener('mousedown', this._onMouseDown.bind(this));
  }
};

FloatPane.prototype.draw = function () {
  if (!this.getMap() || !this._element) {
    return;
  }

  const {position, transform} = this.getPosition();
  const pixelPosition = this.getProjection().fromCoordToOffset(position);

  this._element.style.position = 'absolute';
  this._element.style.left = `${pixelPosition.x}px`;
  this._element.style.top = `${pixelPosition.y}px`;

  if (transform) {
    const {x = 0, y = 0} = transform;
    this._element.style.transform = `translate(${x}, ${y})`;
  }
};

FloatPane.prototype.setZIndex = function (zIndex: number) {
  if (this._element) {
    this._element.style.zIndex = String(zIndex);
  }
};

FloatPane.prototype.onRemove = function () {
  if (this._element) {
    this._element.removeEventListener('mousedown', this._onMouseDown);
    this._element.remove();
  }
};

FloatPane.prototype._onMouseDown = function (event: MouseEvent) {
  event.preventDefault();

  if (!this._draggable) {
    return;
  }

  const map = this.getMap();

  if (!map) {
    return;
  }

  const startMousePosition = {
    x: event.clientX,
    y: event.clientY,
  };

  map.setOptions('draggable', false);

  const onMouseMove = (event: MouseEvent) => {
    if (!this._element) {
      return;
    }

    const deltaX = event.clientX - startMousePosition.x;
    const deltaY = event.clientY - startMousePosition.y;

    const {position} = this.getPosition();
    const pixelPosition = this.getProjection().fromCoordToOffset(position);
    this._element.style.left = `${pixelPosition.x + deltaX}px`;
    this._element.style.top = `${pixelPosition.y + deltaY}px`;
  };

  const onMouseUp = (event: MouseEvent) => {
    // 마우스 이동 거리 계산
    const deltaX = event.clientX - startMousePosition.x;
    const deltaY = event.clientY - startMousePosition.y;
    const projection = this.getProjection();

    // 시작 위치를 기반으로 새로운 픽셀 위치 계산
    const startPixelPosition = this.getProjection().fromCoordToOffset(
      this.getPosition().position
    );
    const finalPixelPosition = new window.naver.maps.Point(
      startPixelPosition.x + deltaX,
      startPixelPosition.y + deltaY
    );

    // 새로운 픽셀 위치에서 좌표 위치로 변환
    const finalCoordPosition = projection.fromOffsetToCoord(finalPixelPosition);

    // 새 위치 설정
    this.setPosition({...this.getPosition(), position: finalCoordPosition});
    this.draw(); // 화면에 적용

    const map = this.getMap();

    if (map) {
      map.setOptions('draggable', true);

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }
  };

  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
};
/**/
